import { z } from 'zod';
import { inferApiResultSchema } from './infer-api';

export const AreaOfResponsibilityMaxLength = 50;

export const DemandReviewerSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  demandId: z.number(),
  reviewerId: z.string().min(1).max(50),
  /**
   * Why this person is reviewing this demand - "Marketing", "Legal".
   *
   * Deliberately not the job title the Demand Card design shows: a title belongs to the person and
   * is constant, whereas the same reviewer can review one demand for Legal and another for
   * Marketing. Nullable, and null for every reviewer assigned before it existed.
   */
  areaOfResponsibility: z.string().max(AreaOfResponsibilityMaxLength).nullable(),
  /** How many decisions this reviewer has recorded. A reviewer with any cannot be removed. */
  decisionCount: z.number(),
  /**
   * The most recent decision, which is the reviewer's current state - what the panel renders beside
   * their name. Null until they have recorded one.
   *
   * A plain string for the same reason as on the decision itself: a value that has stopped being
   * accepted on new records still has to deserialise here.
   */
  latestDecision: z.string().nullable(),
  createdBy: z.string().max(50),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type DemandReviewerDto = z.infer<typeof DemandReviewerSchema>;

export const GetDemandReviewersResponseSchema = inferApiResultSchema(z.array(DemandReviewerSchema));
export type GetDemandReviewersResponseDto = z.infer<typeof GetDemandReviewersResponseSchema>;

export const CreateDemandReviewerSchema = DemandReviewerSchema.pick({
  reviewerId: true,
}).extend({
  // Nullable as well as optional, matching the update schema and the column: a dialogue that leaves
  // the field empty may send either, and neither should be a 400.
  areaOfResponsibility: z.string().max(AreaOfResponsibilityMaxLength).nullable().optional(),
  /**
   * A message from the requester, delivered to the reviewer as a notification and stored nowhere in
   * FIM - no column, and nothing in `activity_logs`. It describes the request, not the assignment,
   * so it has no meaning once the request has been sent.
   *
   * `apps/notification` does persist it in `notification.variables`, so the text outlives the
   * request under notification retention rather than demand history.
   */
  note: z.string().optional(),
});
export type CreateDemandReviewerDto = z.infer<typeof CreateDemandReviewerSchema>;

export const CreateDemandReviewerResponseSchema = inferApiResultSchema(DemandReviewerSchema);
export type CreateDemandReviewerResponseDto = z.infer<typeof CreateDemandReviewerResponseSchema>;

export const UpdateDemandReviewerSchema = CreateDemandReviewerSchema.omit({ note: true })
  .extend({ areaOfResponsibility: z.string().max(AreaOfResponsibilityMaxLength).nullable() })
  .partial()
  .refine((dto) => Object.values(dto).some((value) => value !== undefined), {
    message: 'At least one reviewer field must be provided',
  });
export type UpdateDemandReviewerDto = z.infer<typeof UpdateDemandReviewerSchema>;

export const UpdateDemandReviewerResponseSchema = inferApiResultSchema(DemandReviewerSchema);
export type UpdateDemandReviewerResponseDto = z.infer<typeof UpdateDemandReviewerResponseSchema>;
